// Shared helpers for rolling up the Interest Estimator's rows into the
// same shape the pricing cards, offer generator, marketing package, and
// Deal Tracker kanban all consume. When the estimator carries
// meaningful data for a deal, it becomes the source of truth for
// valuation — downstream views fall back to the ownership_records
// pipeline only when the estimator is empty.

// Convert a decimal / fraction / $-prefixed / percent-suffixed string
// into a number. Matches the parser the Estimator card already uses.
// Exported so downstream callers (Marketing Package builder, Offer
// generator, Pricing card) can coerce raw Estimator row values the same
// way — parseFloat silently truncates fractions ("3/16" → 3), which
// lets MI/LR/WI % drift out of sync with the Estimator's displayed
// totals.
export function num(v) {
  if (v == null) return 0;
  const raw = String(v).trim();
  if (!raw) return 0;
  const frac = raw.match(/(-?\d*\.?\d+)\s*\/\s*(-?\d*\.?\d+)/);
  if (frac) {
    const n = parseFloat(frac[1]);
    const d = parseFloat(frac[2]);
    if (Number.isFinite(n) && Number.isFinite(d) && d !== 0) return n / d;
    return 0;
  }
  const s = raw.replace(/[$,%\s,]/g, '').trim();
  if (!s) return 0;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

// Royalties get typed three ways on this book — "3/16", "0.1875" and
// "18.75%" — and the Preliminary Title royalty field's own placeholder
// invites all three. num() handles the first two but strips a percent
// sign without scaling, so "25%" came back as 25 rather than 0.25 and
// NRA computed as NMA x 25 x 8. The 2x NMA cap then clamped the result,
// which hid the bug at exactly 1/4 (0.25 x 8 = 2.0, the cap itself) and
// overstated NRA at every royalty below it. A royalty is a fraction of
// production and can never exceed 1, so a value above 1 was written as
// a percentage whether or not the sign was typed; a value already
// written as a fraction is its own decimal and is never rescaled.
export function parseRoyaltyFraction(v) {
  const raw = String(v ?? '').trim();
  if (!raw) return 0;
  const parsed = num(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  const writtenAsFraction = /\d\s*\/\s*\d/.test(raw);
  // A slash with nothing usable on both sides of it is a fraction
  // somebody is still typing. num() falls through to parseFloat there
  // and returns the numerator alone, so "3/" would read as 3 and then
  // be rescaled to 0.03. Refuse it instead of guessing.
  if (!writtenAsFraction && raw.includes('/')) return 0;
  const writtenAsPercent = !writtenAsFraction && (/%/.test(raw) || parsed > 1);
  const decimal = writtenAsPercent ? parsed / 100 : parsed;
  return decimal > 0 && decimal <= 1 ? decimal : 0;
}

// The royalties a lease in this basin actually reserves. Used to put a
// typed royalty back into the form a landman reads, so the same royalty
// does not sit in the book as "3/16", "3/16th", "0.1875" and "18.75%"
// on four different rows.
const STANDARD_ROYALTIES = [
  '1/32', '1/16', '3/32', '1/8', '5/32', '3/16', '7/32', '1/5', '1/4', '5/16', '3/8', '1/2',
];

// Canonical stored form for a royalty. Fractions keep their numerator
// and denominator and lose any ordinal suffix ("3/16th" -> "3/16");
// percents and decimals become the standard fraction they equal, or a
// plain decimal when they match none. Anything that will not parse is
// returned untouched — a normaliser that cannot read a value has no
// business overwriting it.
export function normalizeRoyaltyInput(v) {
  const raw = String(v ?? '').trim();
  if (!raw) return '';
  const decimal = parseRoyaltyFraction(raw);
  if (decimal <= 0) return raw;
  const frac = raw.match(/(-?\d*\.?\d+)\s*\/\s*(-?\d*\.?\d+)/);
  if (frac) return `${frac[1]}/${frac[2]}`;
  const standard = STANDARD_ROYALTIES.find((f) => Math.abs(parseRoyaltyFraction(f) - decimal) < 1e-9);
  return standard || String(decimal);
}

export function isMineralInterestType(type) {
  return ['MI', 'NEMI'].includes(String(type || 'MI').trim().toUpperCase());
}

/**
 * An override: a cost-free share of production, carved out of somebody
 * else's leasehold rather than out of the fee.
 *
 * ORRI and PP price identically here, and they have to, because the
 * decimal recorded on the row is already a REVENUE interest in both
 * cases — so NRA equals NMA and no royalty is applied. Run a production
 * payment through the mineral formula instead and it would be divided
 * by the royalty a second time, which prices a real interest at a
 * fraction of what it is worth.
 *
 * What a PP adds is termination: it ends once an agreed sum or volume
 * has been paid out. Nothing in this module models that, deliberately —
 * when it ends depends on prices and rates of production that the
 * estimator has no view of. It belongs in the $/NRA somebody types,
 * which is where every other judgement about a tract's future already
 * lives.
 */
export const OVERRIDE_TYPES = Object.freeze(['ORRI', 'PP']);

export function isOverrideType(type) {
  return OVERRIDE_TYPES.includes(String(type || '').trim().toUpperCase());
}

// Parse "123 / 456" or "123-456" into [book, page] strings. Either part
// can be blank.
function splitBookPage(raw) {
  if (!raw) return ['', ''];
  const parts = String(raw).split(/[\s/-]+/).map((s) => s.trim()).filter(Boolean);
  return [parts[0] || '', parts[1] || ''];
}

// Build an STR label from the row's four new fields. Falls back to
// whatever pieces are populated.
export function estimatorStrLabel(r) {
  const bits = [];
  const state = String(r?.state || '').trim().toUpperCase();
  if (state === 'TX' || r?.abstractNo || r?.surveyName || r?.block || r?.surveyNo) {
    if (r?.abstractNo) bits.push(`A-${r.abstractNo}`);
    if (r?.surveyName) bits.push(r.surveyName);
    if (r?.block) bits.push(`Blk ${r.block}`);
    if (r?.surveyNo) bits.push(`Surv ${r.surveyNo}`);
  } else if (r?.tract || r?.parcelId || r?.unitName || r?.leaseName) {
    if (r?.tract) bits.push(r.tract);
    if (r?.parcelId) bits.push(`Parcel ${r.parcelId}`);
    if (r?.unitName) bits.push(r.unitName);
    if (r?.leaseName) bits.push(r.leaseName);
  } else {
    if (r?.quarterCall) bits.push(r.quarterCall);
    if (r?.section) bits.push(`Sec ${r.section}`);
    if (r?.township) bits.push(r.township);
    if (r?.range) bits.push(r.range);
  }
  return bits.join(' ') || '—';
}

function pricingLinkText(value) {
  return String(value || '').trim().toUpperCase().replace(/\s+/g, ' ');
}

function pricingLinkSection(value) {
  return pricingLinkText(value).replace(/[^0-9]/g, '').replace(/^0+/, '') || pricingLinkText(value);
}

function pricingLinkTownshipRange(value, prefix) {
  return pricingLinkText(value).replace(new RegExp(`^${prefix}`), '');
}

export function estimatorPricingLinkKey(row) {
  if (!row) return '';
  const state = pricingLinkText(row.state);
  const county = pricingLinkText(row.county);
  const abstractNo = pricingLinkText(row.abstractNo).replace(/^A-?/, '');
  const surveyName = pricingLinkText(row.surveyName);
  const block = pricingLinkText(row.block);
  const surveyNo = pricingLinkText(row.surveyNo);
  if (abstractNo || surveyName || block || surveyNo) {
    return [state, county, 'tx', abstractNo, surveyName, block, surveyNo].join('|');
  }
  const tract = pricingLinkText(row.tract);
  const parcelId = pricingLinkText(row.parcelId);
  const unitName = pricingLinkText(row.unitName);
  const leaseName = pricingLinkText(row.leaseName);
  if (tract || parcelId || unitName || leaseName) {
    return [state, county, 'tract', tract, parcelId, unitName, leaseName].join('|');
  }
  const section = pricingLinkSection(row.section);
  const township = pricingLinkTownshipRange(row.township, 'T');
  const range = pricingLinkTownshipRange(row.range, 'R');
  if (!section || !township || !range) return '';
  const quarterCall = pricingLinkText(row.quarterCall);
  return [state, county, 'plss', quarterCall, section, township, range].join('|');
}

function linkedRateValue(value) {
  if (value === null || value === undefined) return '';
  return String(value);
}

function stripPricingLinkFields(row = {}) {
  const {
    pricingLinkedFromDealId,
    pricingLinkedFromRowId,
    pricingLinkedFromKey,
    pricingLinkedMode,
    ...rest
  } = row;
  return rest;
}

export function clearEstimatorPricingLinks(rows = []) {
  return (Array.isArray(rows) ? rows : []).map(stripPricingLinkFields);
}

export function linkEstimatorPricingRows(targetRows = [], sourceRows = [], sourceDealId = '') {
  const sourceByKey = new Map();
  for (const sourceRow of sourceRows || []) {
    if (isEstimatorRowExcluded(sourceRow)) continue;
    const key = estimatorPricingLinkKey(sourceRow);
    if (!key) continue;
    if (!sourceByKey.has(key)) sourceByKey.set(key, []);
    sourceByKey.get(key).push(sourceRow);
  }
  const consumedByKey = new Map();
  let matched = 0;
  let missing = 0;
  let changed = false;
  const rows = (targetRows || []).map((targetRow) => {
    const key = estimatorPricingLinkKey(targetRow);
    if (!key || isEstimatorRowExcluded(targetRow)) return targetRow;
    const matches = sourceByKey.get(key) || [];
    const nextIndex = consumedByKey.get(key) || 0;
    const sourceRow = matches[nextIndex];
    if (!sourceRow) {
      missing += 1;
      return targetRow;
    }
    consumedByKey.set(key, nextIndex + 1);
    matched += 1;
    const nextRow = {
      ...targetRow,
      buyRate: linkedRateValue(sourceRow.buyRate),
      sellRate: linkedRateValue(sourceRow.sellRate),
      pricingLinkedFromDealId: sourceDealId || '',
      pricingLinkedFromRowId: sourceRow.id || '',
      pricingLinkedFromKey: key,
      pricingLinkedMode: 'buy-sell',
    };
    const same = [
      'buyRate',
      'sellRate',
      'pricingLinkedFromDealId',
      'pricingLinkedFromRowId',
      'pricingLinkedFromKey',
      'pricingLinkedMode',
    ].every((field) => String(targetRow?.[field] || '') === String(nextRow?.[field] || ''));
    if (!same) changed = true;
    return same ? targetRow : nextRow;
  });
  return { rows, matched, missing, changed };
}

export const UNLEASED_MINERAL_PRICING = {
  ASSUMED_QUARTER_ROYALTY: 'assumed-quarter-royalty',
  FULL_NMA: 'full-nma',
  // Compulsorily pooled: the owner never signed a lease, the regulator
  // pooled their acreage into a unit anyway, and the pooling order
  // leaves them a statutory royalty on their share — 1/8 in the orders
  // this desk sees. So the interest IS revenue-bearing, at a royalty
  // nobody negotiated, which is neither of the other two scenarios:
  // assuming 1/4 prices it at twice what the order actually pays, and
  // pricing against full NMA treats a producing royalty as if it were
  // unburdened acreage waiting to be leased.
  FORCE_POOLED_STATUTORY_ROYALTY: 'force-pooled-statutory-royalty',
};

export const ASSUMED_UNLEASED_MINERAL_ROYALTY = 0.25;

// 1/8. A pooling order can specify otherwise, and when one does the row
// is better recorded as a leased interest at that royalty — this is the
// default the orders carry, not a cap.
export const FORCE_POOLED_STATUTORY_ROYALTY = 0.125;

export function unleasedMineralPricingScenario(row) {
  const raw = row?.unleasedMineralPricingScenario;
  if (raw === UNLEASED_MINERAL_PRICING.FULL_NMA) return UNLEASED_MINERAL_PRICING.FULL_NMA;
  if (raw === UNLEASED_MINERAL_PRICING.FORCE_POOLED_STATUTORY_ROYALTY) {
    return UNLEASED_MINERAL_PRICING.FORCE_POOLED_STATUTORY_ROYALTY;
  }
  // Anything else, including absent: the assumed quarter. Unchanged, so
  // every row saved before this scenario existed prices as it did.
  return UNLEASED_MINERAL_PRICING.ASSUMED_QUARTER_ROYALTY;
}

// Normalise a Date or a YYYY-MM-DD string to a local YYYY-MM-DD day
// stamp so lease expirations compare as calendar days. Using local
// components rather than toISOString keeps "expired today" from
// flipping a few hours early for anyone west of UTC.
export function estimatorDayStamp(value = new Date()) {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// A row flagged as carrying an active oil and gas lease whose primary
// term has already run out is not automatically mispriced — most such
// leases are held by production. But the two readings price very
// differently: a lease that truly expired and was released reverts to
// unleased minerals, which price on the assumed 1/4 royalty (NRA = NMA
// × 2) instead of the lease's own royalty (a 3/16 lease prices at NRA =
// NMA × 1.5). So the call has to be recorded deliberately, not
// inferred from the dates. True here means the row still needs that
// call made: it claims an active lease, its expiration is in the past,
// and nobody has marked it held by production.
export function leaseNeedsProductionReview(row, asOf = new Date()) {
  if (!isMineralInterestType(row?.type)) return false;
  if (!row?.activeLease) return false;
  if (row?.heldByProduction) return false;
  if (isEstimatorRowExcluded(row)) return false;
  const expires = String(row?.leaseExpirationDate || '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(expires)) return false;
  const today = estimatorDayStamp(asOf);
  if (!today) return false;
  return expires < today;
}

// Rows across a deal (or a whole book) that still need the
// held-by-production call made.
export function leaseProductionReviewRows(rows = [], asOf = new Date()) {
  return (Array.isArray(rows) ? rows : []).filter((row) => leaseNeedsProductionReview(row, asOf));
}

// What an unpriced investor row is worth, until somebody prices it.
//
// Paraclete's sell rate is what a tract is offered to the open market
// for. An investor placing capital takes the whole package and takes it
// now, so they buy under that — the discount IS the offer, which is why
// it lives in the price rather than in a note somebody has to apply.
//
// This is a PLACEHOLDER, not a pricing model. A deal-level discount
// percentage used to sit at the top of the estimator and stamp this
// number onto every placed row; it was removed because that is not how
// the desk works. Nobody agrees a percentage — somebody agrees a figure
// for a tract, and the percentage is the arithmetic afterwards. The one
// deal that ever used the field carried 11.5435%, reverse-engineered to
// make a total come out right, which made the case better than any
// argument could.
//
// So the real input is investorSalePrice, typed per row, and the
// discount is its OUTPUT. This constant only answers "what is this row
// worth?" for a placed row nobody has priced yet, so the estimator has
// something to show instead of the full open-market quote.
export const INVESTOR_SELL_DISCOUNT_PCT = 0.15;

/**
 * The price this row was actually sold to the investor for, in dollars.
 *
 * The negotiated number, typed on the row. A deal is not priced by
 * applying a tidy percentage to a quote — somebody agrees a figure for
 * a tract — so this is the input and the discount is the OUTPUT, which
 * is the way round the desk actually works.
 *
 * A TOTAL, not a rate. The estimator prices everything else per NRA or
 * per NMA, and this is the one money box that does not, because "we
 * sold them that tract for eighty-five thousand" is the sentence being
 * recorded. Zero is a real answer (a tract thrown in), so only a blank
 * means "not individually priced" and falls through to the deal rate.
 */
export function investorSalePriceOf(row) {
  const raw = row?.investorSalePrice;
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim();
  if (!text) return null;
  // Number(), deliberately NOT this module's num(). num() answers 0 for
  // anything it cannot read, which is right for a rate field — a blank
  // sell rate is an unpriced row — and catastrophic here, where 0 is a
  // real price meaning "thrown in free". A typo would be stored as a
  // giveaway. Number() answers NaN for 'abc', '12k' and 'TBD' alike,
  // which is the whole guard; a regex beside it only said the same
  // thing twice.
  const value = Number(text.replace(/[$,\s]/g, ''));
  if (!Number.isFinite(value) || value < 0) return null;
  return value;
}

/** True when this row is being sold to an investor. */
export function rowSellsToInvestor(row) {
  return !!String(row?.investorId || '').trim() && !isEstimatorRowExcluded(row);
}

/**
 * The rate this row actually sells at, and what it was quoted at.
 *
 * Both, because the discount has to be shown rather than silently
 * applied: a sell price that quietly dropped reads as a pricing
 * mistake. The UI strikes the quoted rate through and prints the
 * discount beside it — and since the discount now varies by deal, that
 * printed number is the only place the agreed rate is visible on the
 * row.
 */
export function investorSellPricing(row, { unit = null } = {}) {
  const quoted = num(row?.sellRate);
  if (!rowSellsToInvestor(row) || !(quoted > 0)) {
    return {
      quotedRate: quoted, sellRate: quoted, discountPct: 0, discounted: false,
      salePrice: null, salePriceTotal: null,
    };
  }

  // A price typed on the row beats any percentage. The percentage was
  // only ever a way to arrive at a number; when somebody has agreed the
  // number, deriving a different one from a stale rate would overwrite
  // what was actually sold.
  const salePrice = investorSalePriceOf(row);
  if (salePrice !== null && Number.isFinite(unit) && unit > 0) {
    const marketTotal = quoted * unit;
    const impliedRate = salePrice / unit;
    // Negative when the row sold ABOVE market, which happens and is
    // worth seeing — the UI reads a negative discount as a premium
    // rather than hiding it.
    const discountPct = marketTotal > 0 ? 1 - (salePrice / marketTotal) : 0;
    return {
      quotedRate: quoted,
      sellRate: impliedRate,
      discountPct,
      discounted: Math.abs(discountPct) > 1e-9,
      salePrice,
      salePriceTotal: salePrice,
    };
  }
  // A price typed on a row with nothing to divide by — no acreage, no
  // interest — cannot become a rate. The money is still what was
  // agreed, so it is carried as the total and the rate stays at the
  // quote rather than becoming Infinity.
  if (salePrice !== null) {
    return {
      quotedRate: quoted, sellRate: quoted, discountPct: 0, discounted: false,
      salePrice, salePriceTotal: salePrice,
    };
  }

  // Nothing typed on the row, so the placeholder stands in. There is no
  // per-deal rate to consult any more and no stamp to read off the row:
  // a placed row is either priced or it is at the default.
  const discount = INVESTOR_SELL_DISCOUNT_PCT;
  return {
    quotedRate: quoted,
    sellRate: quoted * (1 - discount),
    discountPct: discount,
    discounted: discount > 0,
    salePrice: null,
    salePriceTotal: null,
  };
}

export function isEstimatorRowIgnored(row) {
  return !!row?.ignored;
}

export function isEstimatorRowExcluded(row) {
  return !!row?.sold || isEstimatorRowIgnored(row);
}

/**
 * How much of the interest we ACQUIRE. Shown as "Buy %".
 *
 * Scales the row's acreage, so it moves the buy side and the sell side
 * together — which is right, because a half interest bought is a half
 * interest to sell. It answers "we are taking 50% of what they own".
 *
 * It does NOT answer "we bought all of it and sold part of it". That is
 * estimatorSoldFraction below, and keeping the two apart is the whole
 * point: one shrinks what we own, the other shrinks what leaves.
 */
export function estimatorSaleFraction(row) {
  const raw = row?.salePct ?? row?.purchasePct ?? row?.partialSalePct ?? row?.interestSalePct;
  if (raw === null || raw === undefined || raw === '') return 1;
  const parsed = num(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  const fraction = parsed > 1 ? parsed / 100 : parsed;
  return Math.max(0, Math.min(1, fraction));
}

/**
 * How much of what we bought is CONVEYED ON. Shown as "Sold %".
 *
 * Paraclete buys 100% of an interest and sells a slice of it, keeping the
 * rest as a held asset. Only the sell side shrinks: the buy total stays at
 * the full acquisition, because that is the money that actually left.
 *
 * Blank means 100 — sell everything we bought, which is what every row
 * written before this field existed did, so they price unchanged.
 *
 * Parsed like a percentage and NOT through a bare num(): num() answers 0
 * for anything it cannot read, and 0 here means "sold none of it", which
 * would silently zero a row's whole sell side on a typo. Unreadable input
 * falls back to selling all of it — the previous behaviour — rather than
 * to nothing.
 */
/**
 * Is this row a PART sale — somebody typed a soldPct — as opposed to a
 * row that never used the field?
 *
 * The difference matters and estimatorSoldFraction cannot carry it: an
 * absent soldPct answers 1 there, meaning "sold all of it", which is the
 * right default for pricing and the wrong one for asking what is still
 * held. Every row written before part sales existed has no soldPct, so
 * reading those as "retained nothing" would empty the book.
 *
 * So: explicit and readable and under 100. Anything else is not a part
 * sale, including an unreadable value — estimatorSoldFraction already
 * falls back to selling all of it there, and the two must agree.
 */
export function estimatorPartSale(row) {
  const raw = row?.soldPct;
  if (raw === null || raw === undefined) return false;
  const text = String(raw).trim().replace(/%$/, '').trim();
  if (!text) return false;
  if (!/^\d+(\.\d+)?$/.test(text)) return false;
  const parsed = Number(text);
  return Number.isFinite(parsed) && parsed < 100;
}

export function estimatorSoldFraction(row) {
  const raw = row?.soldPct;
  if (raw === null || raw === undefined) return 1;
  const text = String(raw).trim().replace(/%$/, '').trim();
  if (!text) return 1;
  if (!/^\d+(\.\d+)?$/.test(text)) return 1;
  const parsed = Number(text);
  if (!Number.isFinite(parsed)) return 1;
  // A percentage, always. Unlike Buy % above there is no "0.4 means 40%"
  // shorthand: this field is new, so nothing stored uses it, and the
  // shorthand is what makes "1" ambiguous between 1% and 100%.
  return Math.max(0, Math.min(1, parsed / 100));
}

// Derive per-row computed fields. MI/NEMI/ORRI/PP/NPRI rows use NMA = gross × decimal.
// LEASE rows model taking an oil & gas lease on a mineral interest —
// bonus priced per NMA (gross × decimal), no NRA; the royalty field
// records the royalty reserved in the new lease, informational only.
// MI/NEMI rows use NRA = NMA × LR × 8 only when the lease is active, defaulting
// blank active-lease royalty to 1/4. NPRI rows can use a typed royalty
// fraction but never carry active leasing rights.
// ORRI and PP rows price against NRA, but their NRA equals NMA.
// NRA is capped at 2× NMA for every non-WI row.
// WI rows use NMA = gross × MI decimal × WI% (net WI acres), because
// the working-interest percentage applies to the specific mineral
// interest amount captured on the row. WI economics are priced on NMA
// only; NRI is stored as an informational field and does not create NRA.
export function computeEstimatorRow(r) {
  const type = String(r?.type || 'MI').trim().toUpperCase();
  const isMI = isMineralInterestType(type);
  const isNPRI = type === 'NPRI';
  // ORRI and PP: both already revenue interests, so both take the
  // override branch below. See isOverrideType.
  const isORRI = isOverrideType(type);
  const isProductionPayment = type === 'PP';
  const isWI = type === 'WI';
  const isMineralLease = type === 'LEASE';
  const isUnleasedMI = isMI && !r?.activeLease;
  const unleasedPricingScenario = isUnleasedMI ? unleasedMineralPricingScenario(r) : null;
  const gross = num(r?.grossAcreage);
  const saleFraction = estimatorSaleFraction(r);
  const salePct = saleFraction * 100;
  const buyRate = num(r?.buyRate);
  let nma = 0;
  let nra = 0;
  let decimal = 0;
  let leaseRoyalty = 0;
  let wiPct = 0;
  let nriPct = 0;
  if (isWI) {
    decimal = num(r?.mi);
    wiPct = num(r?.workingInterestPct);
    nriPct = num(r?.nri);
    nma = gross * decimal * (wiPct / 100) * saleFraction;
    nra = 0;
  } else {
    decimal = num(r?.mi);
    leaseRoyalty = parseRoyaltyFraction(r?.leaseRoyalty);
    if (isMI && r?.activeLease && leaseRoyalty <= 0) {
      leaseRoyalty = ASSUMED_UNLEASED_MINERAL_ROYALTY;
    }
    nma = gross * decimal * saleFraction;
    if (nma > 0) {
      if (isORRI) {
        nra = nma;
      } else if (isMineralLease) {
        nra = 0;
      } else if (isMI && r?.activeLease && leaseRoyalty > 0) {
        nra = nma * leaseRoyalty * 8;
      } else if (isNPRI && leaseRoyalty > 0) {
        nra = nma * leaseRoyalty * 8;
      } else if (isUnleasedMI && unleasedPricingScenario === UNLEASED_MINERAL_PRICING.FORCE_POOLED_STATUTORY_ROYALTY) {
        // The same arithmetic every royalty gets here, at the statutory
        // 1/8: NRA = NMA × 1/8 × 8, which comes to NMA exactly. Worth
        // writing as the formula rather than the answer, so it follows
        // the constant if an order ever makes it something else.
        nra = nma * FORCE_POOLED_STATUTORY_ROYALTY * 8;
      } else if (isUnleasedMI && unleasedPricingScenario === UNLEASED_MINERAL_PRICING.ASSUMED_QUARTER_ROYALTY) {
        nra = nma * ASSUMED_UNLEASED_MINERAL_ROYALTY * 8;
      } else if (isUnleasedMI && unleasedPricingScenario === UNLEASED_MINERAL_PRICING.FULL_NMA) {
        nra = 0;
      } else {
        nra = nma * 8;
      }
      const nraCap = nma * 2;
      nra = Math.min(nra, nraCap);
    }
  }
  // Pricing basis — MI/NEMI/NPRI/ORRI/PP prices against NRA, WI against NMA. This
  // is the number (rate × unit) we sum for totals.
  const pricingUnit = isWI || isMineralLease || (isUnleasedMI && unleasedPricingScenario === UNLEASED_MINERAL_PRICING.FULL_NMA) ? 'nma' : 'nra';
  const unit = pricingUnit === 'nma' ? nma : nra;
  // Sell pricing is derived HERE, below `unit`, and not above with the
  // other rates — because a row can carry a negotiated sale price in
  // dollars, and turning that into a per-unit rate needs the unit. It
  // used to sit above, which was fine while the only input was a
  // percentage.
  // Buy all of it, sell a slice. `unit` is what we own; `soldUnit` is what
  // leaves. The sell side is priced on soldUnit and the buy side is not,
  // because the full purchase price is the money that actually went out.
  const soldFraction = estimatorSoldFraction(r);
  const soldPct = soldFraction * 100;
  const soldUnit = unit * soldFraction;
  const retainedUnit = unit - soldUnit;
  const soldNma = nma * soldFraction;
  const soldNra = nra * soldFraction;
  const retainedNma = nma - soldNma;
  const retainedNra = nra - soldNra;
  // Priced against the SOLD unit: a typed sale price is the money for the
  // slice that left, so the rate it implies and the discount it represents
  // are both per sold unit. Passing the whole unit would read a part sale
  // as a deep discount on the entire interest.
  const {
    quotedRate: quotedSellRate, sellRate, discountPct: sellDiscountPct,
    discounted: sellDiscounted, salePrice: investorSalePrice,
  } = investorSellPricing(r, { unit: soldUnit });
  const buyTotal = buyRate * unit;
  // What the sold slice cost, and what the retained slice cost. Margin is
  // taken against the first: the retained share has not been sold, so its
  // cost is inventory rather than a loss, and it earns its own margin when
  // it sells. Charging the whole purchase against a part sale would read a
  // profitable deal as a loss and pay nobody.
  const soldBuyTotal = buyRate * soldUnit;
  const retainedBuyTotal = buyTotal - soldBuyTotal;
  // The typed price IS the total. Deriving it back from the implied
  // rate would reintroduce the rounding the typed figure exists to
  // avoid, and on a row with no unit there is no rate to derive from.
  const sellTotal = investorSalePrice !== null ? investorSalePrice : sellRate * soldUnit;
  // What the row would have fetched at the quoted rate — the thing the
  // discount is a discount FROM, and what the UI strikes through.
  const marketTotal = quotedSellRate * soldUnit;
  const margin = sellTotal - soldBuyTotal;
  const [leaseBook, leasePage] = splitBookPage(r?.bookPage);
  return {
    // passthrough identifiers
    id: r?.id,
    type, isMI, isNPRI, isORRI, isProductionPayment, isWI, isMineralLease, pricingReady: !!r?.pricingReady, sold: !!r?.sold, ignored: isEstimatorRowIgnored(r), excluded: isEstimatorRowExcluded(r), activeLease: isMI && !!r?.activeLease, leasePdfDocId: isMI ? r?.leasePdfDocId || null : null,
    isUnleasedMI,
    unleasedPricingScenario,
    pricingUnit,
    state: r?.state || '',
    county: r?.county || '',
    // Land description
    quarterCall: r?.quarterCall || '',
    section: r?.section || '',
    township: r?.township || '',
    range: r?.range || '',
    abstractNo: r?.abstractNo || '',
    surveyName: r?.surveyName || '',
    block: r?.block || '',
    surveyNo: r?.surveyNo || '',
    tract: r?.tract || '',
    parcelId: r?.parcelId || '',
    unitName: r?.unitName || '',
    leaseName: r?.leaseName || '',
    // inputs
    gross,
    decimal,
    leaseRoyalty,
    wiPct,
    nriPct,
    saleFraction,
    salePct,
    effectiveDecimal: decimal * saleFraction,
    effectiveWiPct: wiPct * saleFraction,
    effectiveNriPct: nriPct * saleFraction,
    depthTop: r?.depthTop || '',
    depthBase: r?.depthBase || '',
    leaseDate: r?.leaseDate || '',
    primaryTerm: r?.primaryTerm || '',
    leaseExpirationDate: r?.leaseExpirationDate || '',
    heldByProduction: !!r?.heldByProduction,
    buyRate,
    sellRate,
    quotedSellRate,
    sellDiscountPct,
    sellDiscounted,
    investorSalePrice,
    marketTotal,
    leaseBook,
    leasePage,
    // derived
    nma, nra, unit, buyTotal, sellTotal,
    // The part sale. soldPct is 100 and retained* are 0 on every row that
    // does not use it, so a full sale reads exactly as it always did.
    soldFraction, soldPct, soldUnit, retainedUnit,
    soldNma, soldNra, retainedNma, retainedNra,
    soldBuyTotal, retainedBuyTotal,
    margin,
  };
}

// True when at least one active row has a real interest number
// entered. Used by downstream consumers to decide whether to trust
// the Estimator as the source of truth or fall back to the
// ownership_records pipeline.
export function estimatorHasData(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  return rows.some((r) => {
    const c = computeEstimatorRow(r);
    return !c.excluded && (c.nma > 0 || c.nra > 0);
  });
}

// Roll up the estimator into MI + WI buckets. Returned shape is
// deliberately compatible with the pricingRollup / wiPricingRollup
// memos in OwnerDashboard so the cards can swap their data source
// without changing the render.
export function rollupEstimator(rows) {
  const mi = {
    buyTotal: 0, sellTotal: 0,
    // soldBuyTotal is what the conveyed share cost and is what margin is
    // taken against; retainedBuyTotal is inventory Paraclete still holds.
    // On a row sold in full the first equals buyTotal and the second is 0.
    soldBuyTotal: 0, retainedBuyTotal: 0,
    soldNma: 0, soldNra: 0, retainedNma: 0, retainedNra: 0,
    ratedNRA: 0, ratedBuyNRA: 0, ratedSellNRA: 0,
    acres: 0, nma: 0, nra: 0,
    rows: [],
  };
  const wi = {
    buyTotal: 0, sellTotal: 0,
    soldBuyTotal: 0, retainedBuyTotal: 0,
    soldNma: 0, soldNra: 0, retainedNma: 0, retainedNra: 0,
    ratedNMA: 0, ratedBuyNMA: 0, ratedSellNMA: 0,
    grossAcres: 0, netWIAcres: 0, netRevenueAcres: 0,
    wiSum: 0, wiCount: 0, nriSum: 0, nriCount: 0,
    rows: [],
  };
  for (const raw of rows || []) {
    const c = computeEstimatorRow(raw);
    if (c.excluded) continue;
    if (c.isWI) {
      wi.grossAcres += c.gross;
      wi.netWIAcres += c.nma;
      wi.netRevenueAcres += c.nra;
      wi.buyTotal += c.buyTotal;
      wi.sellTotal += c.sellTotal;
      wi.soldBuyTotal += c.soldBuyTotal;
      wi.retainedBuyTotal += c.retainedBuyTotal;
      wi.soldNma += c.soldNma;
      wi.soldNra += c.soldNra;
      wi.retainedNma += c.retainedNma;
      wi.retainedNra += c.retainedNra;
      if (c.buyRate > 0) wi.ratedBuyNMA += c.nma;
      if (c.sellRate > 0) wi.ratedSellNMA += c.nma;
      if (c.buyRate > 0 || c.sellRate > 0) wi.ratedNMA += c.nma;
      if (c.wiPct > 0) { wi.wiSum += c.wiPct; wi.wiCount += 1; }
      if (c.nriPct > 0) { wi.nriSum += c.nriPct; wi.nriCount += 1; }
      wi.rows.push(c);
    } else {
      mi.acres += c.gross;
      mi.nma += c.nma;
      mi.nra += c.nra;
      mi.buyTotal += c.buyTotal;
      mi.sellTotal += c.sellTotal;
      mi.soldBuyTotal += c.soldBuyTotal;
      mi.retainedBuyTotal += c.retainedBuyTotal;
      mi.soldNma += c.soldNma;
      mi.soldNra += c.soldNra;
      mi.retainedNma += c.retainedNma;
      mi.retainedNra += c.retainedNra;
      if (c.pricingUnit === 'nma') {
        if (c.buyRate > 0) mi.ratedBuyNRA += c.nma;
        if (c.sellRate > 0) mi.ratedSellNRA += c.nma;
        if (c.buyRate > 0 || c.sellRate > 0) mi.ratedNRA += c.nma;
      } else {
        if (c.buyRate > 0) mi.ratedBuyNRA += c.nra;
        if (c.sellRate > 0) mi.ratedSellNRA += c.nra;
        if (c.buyRate > 0 || c.sellRate > 0) mi.ratedNRA += c.nra;
      }
      mi.rows.push(c);
    }
  }
  // Against what the SOLD share cost. Identical to sellTotal - buyTotal on
  // every deal that sells everything it buys, which is all of them until
  // somebody types a Sold %; on a part sale it stops charging the retained
  // inventory against the slice that was sold.
  const miMargin = mi.sellTotal - mi.soldBuyTotal;
  const wiMargin = wi.sellTotal - wi.soldBuyTotal;
  return {
    mi: {
      ...mi,
      margin: miMargin,
      marginPct: mi.soldBuyTotal > 0 ? (miMargin / mi.soldBuyTotal) * 100 : 0,
      avgBuyRate: mi.ratedBuyNRA > 0 ? mi.buyTotal / mi.ratedBuyNRA : 0,
      avgSellRate: mi.ratedSellNRA > 0 ? mi.sellTotal / mi.ratedSellNRA : 0,
    },
    wi: {
      ...wi,
      margin: wiMargin,
      marginPct: wi.soldBuyTotal > 0 ? (wiMargin / wi.soldBuyTotal) * 100 : 0,
      avgBuyRate: wi.ratedBuyNMA > 0 ? wi.buyTotal / wi.ratedBuyNMA : 0,
      avgSellRate: wi.ratedSellNMA > 0 ? wi.sellTotal / wi.ratedSellNMA : 0,
      avgWI: wi.wiCount > 0 ? wi.wiSum / wi.wiCount : 0,
      avgNRI: wi.nriCount > 0 ? wi.nriSum / wi.nriCount : 0,
      hasAny: wi.rows.length > 0,
    },
    combinedBuy: mi.buyTotal + wi.buyTotal,
    combinedSell: mi.sellTotal + wi.sellTotal,
    combinedSoldBuy: mi.soldBuyTotal + wi.soldBuyTotal,
    combinedRetainedBuy: mi.retainedBuyTotal + wi.retainedBuyTotal,
    combinedMargin: miMargin + wiMargin,
  };
}
